import { AddCircle } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Alert,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { FeeType } from '../../../enums/experience';
import { ExperienceCharge } from '../../../types/experience';
import { ModalProps } from '../../../types/modal';
import { FeesSchema } from '../Form/Pricing/schema';
import ExperienceFeesForm from './ExperienceFeesForm';

export type Fees = {
    fees: Array<ExperienceCharge>;
};

interface Props extends ModalProps {
    experienceType: string;
    fees: Array<ExperienceCharge>;
    onSubmit: (data: Fees) => void;
}

const FeesModal = ({
    open,
    onClose,
    onSubmit,
    fees,
    experienceType,
}: Props) => {
    const [cancel, setCancel] = useState<boolean>(false);

    const methods = useForm<Fees>({
        resolver: zodResolver(FeesSchema),
        defaultValues: { fees: [] },
    });

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = methods;

    const { fields, append, remove } = useFieldArray({
        name: 'fees',
        control,
    });

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        setCancel(false);

        return handleSubmit(onSubmit)(event);
    };

    const handleAdd = () => {
        append({
            feeId: '',
            type: FeeType.Fixed,
            chargeType: 'fee',
            unitPrice: 0,
        });
    };

    const handleDelete = (index: number) => {
        remove(index);
    };

    const handleCancel = () => {
        if (isDirty) {
            // if form is dirty, display alert
            if (!cancel) {
                setCancel(true);
                return;
            }
        }

        if (cancel) reset();

        setCancel(false);
        onClose();
    };

    useEffect(() => {
        if (fees.length > 0) {
            reset({ fees: fees });
        }
    }, [fees]);

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth='md' fullWidth>
            <FormProvider {...methods}>
                <DialogTitle>
                    Additional Charges
                    <IconButton
                        onClick={handleCancel}
                        size='small'
                        aria-label='close'
                        sx={{ color: 'text.primary' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <form id='fees-modal-form' onSubmit={onNestedSubmit}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <DialogContentText
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    fontSize: '14px',
                                }}
                            >
                                Add additional charges such as cleaning or pets.{' '}
                                <strong>Note:</strong> all prices are excluding
                                GST and in AUD.
                            </DialogContentText>
                            <CustomIconButton
                                variant='contained'
                                color='primary'
                                onClick={handleAdd}
                                aria-label='Add Charge'
                            >
                                <AddCircle />
                            </CustomIconButton>
                        </Box>
                        <Collapse in={cancel}>
                            <Alert severity='warning'>
                                You have unsaved changes. Are you sure you want
                                to cancel?
                            </Alert>
                        </Collapse>
                        <Stack spacing={1} pt={2}>
                            {fields.map(
                                (fee: ExperienceCharge, index: number) => (
                                    <ExperienceFeesForm
                                        key={index}
                                        experienceType={experienceType}
                                        index={index}
                                        handleDelete={() => handleDelete(index)}
                                    />
                                ),
                            )}
                        </Stack>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button
                        variant='contained'
                        type='submit'
                        form='fees-modal-form'
                    >
                        Save
                    </Button>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default FeesModal;
